import {
  FlowEdge, FlowGraphData, FlowNode, FlowTrace, LANES, LANE_LABELS, TraceStep, num,
} from './flowTypes';

/**
 * Pure geometry for the Search-flow tab: where every node, sub-step and edge
 * goes, and the animation timeline that moves a pulse along a path. No DOM —
 * the edge paths are sampled here, so the travelling dot is placed from these
 * numbers rather than from getPointAtLength on a mounted <path>.
 *
 * Layout is lane + order, as the server sends them. Two orientations:
 *   'lr'  lanes are columns left -> right, nodes in a lane top -> bottom
 *   'tb'  lanes are rows top -> bottom, nodes in a lane left -> right
 * Everything is computed in (m, c) space — m along the lanes, c across them —
 * and mapped to (x, y) at the end, so one routine serves both.
 */

export type Orientation = 'lr' | 'tb';
export interface Pt { x: number; y: number }
export interface Box { x: number; y: number; w: number; h: number }
export interface NodeBox extends Box { id: string; lane: number; idx: number; node: FlowNode }
export interface SubBox extends Box { nodeId: string; subId: string }
export interface LaneBand { lane: string; label: string; box: Box; labelAt: Pt; index: number }
export interface EdgeGeom {
  id: string; edge: FlowEdge; from: string; to: string;
  d: string; pts: Pt[]; cum: number[]; len: number;
  /** where the edge's label goes, and how it is anchored there */
  label: Pt; labelAnchor: 'start' | 'middle' | 'end';
}
export interface Layout {
  orientation: Orientation;
  width: number;
  height: number;
  nodes: Map<string, NodeBox>;
  subs: Map<string, SubBox>;
  lanes: LaneBand[];
  edges: Map<string, EdgeGeom>;
  /** sub-step id -> its node id, so a path or edge naming a sub-step still lands. */
  parentOf: Map<string, string>;
}

const K = {
  PAD: 18,
  HEADER: 26,          // lr: lane titles above the columns
  GUTTER: 92,          // tb: lane titles left of the rows
  LANE_GAP_LR: 66,
  LANE_GAP_TB: 58,
  NODE_GAP: 24,
  NODE_W: 158,
  NODE_H: 60,
  DEC_W: 150,
  GH: 38,              // group header
  GP: 8,               // group padding
  SG: 6,               // gap between sub-steps
  SUB_LR: { w: 164, h: 40 },
  SUB_TB: { w: 138, h: 46 },
};

type MC = { m0: number; m1: number; c0: number; c1: number; mm: number; cm: number; lane: number; idx: number };
type P = { m: number; c: number };
type Cubic = [P, P, P, P];

export const isParallel = (n: FlowNode) => n.kind === 'parallel' && (n.substeps?.length || 0) > 0;

/** tb: up to three sub-steps side by side; four or more wrap into two rows,
 *  which keeps the understand lane from setting the width of the whole graph. */
const tbGrid = (k: number) => (k <= 3 ? { cols: k, rows: 1 } : { cols: Math.ceil(k / 2), rows: 2 });

function nodeSize(n: FlowNode, o: Orientation): { w: number; h: number } {
  if (isParallel(n)) {
    const k = n.substeps!.length;
    if (o === 'lr') {
      return { w: K.SUB_LR.w + 2 * K.GP, h: K.GH + k * K.SUB_LR.h + (k - 1) * K.SG + K.GP };
    }
    const { cols, rows } = tbGrid(k);
    return { w: cols * K.SUB_TB.w + (cols - 1) * K.SG + 2 * K.GP,
             h: K.GH + rows * K.SUB_TB.h + (rows - 1) * K.SG + K.GP };
  }
  return { w: n.kind === 'decision' ? K.DEC_W : K.NODE_W, h: K.NODE_H };
}

export function laneOrder(nodes: FlowNode[]): string[] {
  const seen: string[] = [];
  for (const l of LANES) if (nodes.some(n => n.lane === l)) seen.push(l);
  for (const n of nodes) if (!seen.includes(n.lane)) seen.push(n.lane);
  return seen;
}

// ---------------------------------------------------------------------------
// cubic helpers
// ---------------------------------------------------------------------------
const lerp = (a: P, b: P, t: number): P => ({ m: a.m + (b.m - a.m) * t, c: a.c + (b.c - a.c) * t });
const line = (a: P, b: P): Cubic => [a, lerp(a, b, 1 / 3), lerp(a, b, 2 / 3), b];
const sCurve = (a: P, b: P): Cubic => {
  const dm = (b.m - a.m) / 2;
  return [a, { m: a.m + dm, c: a.c }, { m: b.m - dm, c: b.c }, b];
};

/** A polyline with rounded corners, as cubics. */
function rounded(points: P[], r = 14): Cubic[] {
  const out: Cubic[] = [];
  let cur = points[0];
  for (let i = 1; i < points.length - 1; i++) {
    const p = points[i]; const prev = points[i - 1]; const next = points[i + 1];
    const dIn = Math.hypot(p.m - prev.m, p.c - prev.c) || 1;
    const dOut = Math.hypot(next.m - p.m, next.c - p.c) || 1;
    const rr = Math.min(r, dIn / 2, dOut / 2);
    const a = { m: p.m - ((p.m - prev.m) / dIn) * rr, c: p.c - ((p.c - prev.c) / dIn) * rr };
    const b = { m: p.m + ((next.m - p.m) / dOut) * rr, c: p.c + ((next.c - p.c) / dOut) * rr };
    out.push(line(cur, a));
    out.push([a, p, p, b]);
    cur = b;
  }
  out.push(line(cur, points[points.length - 1]));
  return out;
}

const bez = (q: Cubic, t: number): P => {
  const u = 1 - t;
  const a = u * u * u, b = 3 * u * u * t, c = 3 * u * t * t, d = t * t * t;
  return { m: a * q[0].m + b * q[1].m + c * q[2].m + d * q[3].m,
           c: a * q[0].c + b * q[1].c + c * q[2].c + d * q[3].c };
};

// ---------------------------------------------------------------------------
// layout
// ---------------------------------------------------------------------------
export function computeLayout(g: FlowGraphData, o: Orientation): Layout {
  // Two passes: the first finds how many bypass edges need the "min" side
  // (left in tb, top in lr) and the second reserves exactly that margin.
  const first = layoutPass(g, o, 0);
  if (!first.minBuses) return first.layout;
  return layoutPass(g, o, 14 + 12 * first.minBuses).layout;
}

function layoutPass(g: FlowGraphData, o: Orientation, minMargin: number): { layout: Layout; minBuses: number } {
  const lanesUsed = laneOrder(g.nodes);
  const laneIdx = new Map(lanesUsed.map((l, i) => [l, i]));
  const byLane: FlowNode[][] = lanesUsed.map(() => []);
  g.nodes.forEach(n => byLane[laneIdx.get(n.lane)!].push(n));
  const inputIdx = new Map(g.nodes.map((n, i) => [n.id, i]));
  byLane.forEach(list => list.sort((a, b) =>
    (num(a.order) ?? 0) - (num(b.order) ?? 0) || inputIdx.get(a.id)! - inputIdx.get(b.id)!));

  const parentOf = new Map<string, string>();
  g.nodes.forEach(n => (n.substeps || []).forEach(s => { if (!parentOf.has(s.id)) parentOf.set(s.id, n.id); }));
  const nodeIds = new Set(g.nodes.map(n => n.id));
  const resolve = (id: string) => (nodeIds.has(id) ? id : parentOf.get(id) || id);
  const pairKey = (a: string, b: string) => (a < b ? `${a}|${b}` : `${b}|${a}`);
  const linked = new Set(g.edges.map(e => pairKey(resolve(e.from), resolve(e.to))));

  const size = new Map(g.nodes.map(n => [n.id, nodeSize(n, o)]));
  const mainOf = (id: string) => (o === 'lr' ? size.get(id)!.w : size.get(id)!.h);
  const crossOf = (id: string) => (o === 'lr' ? size.get(id)!.h : size.get(id)!.w);
  const laneGap = o === 'lr' ? K.LANE_GAP_LR : K.LANE_GAP_TB;
  // Neighbours in a lane joined by an edge get room for the arrow and its label.
  const linkGap = o === 'lr' ? 44 : 58;
  const gapAfter = byLane.map(list => list.map((n, i) =>
    (i < list.length - 1 && linked.has(pairKey(n.id, list[i + 1].id)) ? linkGap : K.NODE_GAP)));

  const laneMain = byLane.map(list => Math.max(0, ...list.map(n => mainOf(n.id))));
  const laneCross = byLane.map((list, li) =>
    list.reduce((a, n, i) => a + crossOf(n.id) + (i < list.length - 1 ? gapAfter[li][i] : 0), 0));
  const totalCross = Math.max(0, ...laneCross);
  const mStart = K.PAD;
  const cStart = (o === 'lr' ? K.PAD + K.HEADER + 6 : K.PAD + K.GUTTER) + minMargin;

  const laneM0: number[] = [];
  let acc = mStart;
  laneMain.forEach((w, i) => { laneM0[i] = acc; acc += w + laneGap; });
  const mEnd = acc - laneGap;

  const mc = new Map<string, MC>();
  byLane.forEach((list, li) => {
    let c = cStart + (totalCross - laneCross[li]) / 2;
    list.forEach((n, idx) => {
      const mSz = mainOf(n.id); const cSz = crossOf(n.id);
      const m0 = laneM0[li] + (laneMain[li] - mSz) / 2;
      mc.set(n.id, { m0, m1: m0 + mSz, c0: c, c1: c + cSz, mm: m0 + mSz / 2, cm: c + cSz / 2, lane: li, idx });
      c += cSz + gapAfter[li][idx];
    });
  });

  const toXY = (p: P): Pt => (o === 'lr' ? { x: p.m, y: p.c } : { x: p.c, y: p.m });
  const nodes = new Map<string, NodeBox>();
  const byId = new Map(g.nodes.map(n => [n.id, n]));
  mc.forEach((b, id) => {
    const tl = toXY({ m: b.m0, c: b.c0 });
    const s = size.get(id)!;
    nodes.set(id, { id, x: tl.x, y: tl.y, w: s.w, h: s.h, lane: b.lane, idx: b.idx, node: byId.get(id)! });
  });

  const subs = new Map<string, SubBox>();
  g.nodes.forEach(n => {
    if (!isParallel(n)) return;
    const b = nodes.get(n.id)!;
    n.substeps!.forEach((s, i) => {
      const key = `${n.id}/${s.id}`;
      if (o === 'lr') {
        subs.set(key, { nodeId: n.id, subId: s.id, x: b.x + K.GP, y: b.y + K.GH + i * (K.SUB_LR.h + K.SG),
                        w: b.w - 2 * K.GP, h: K.SUB_LR.h });
      } else {
        const { cols } = tbGrid(n.substeps!.length);
        const col = i % cols; const row = Math.floor(i / cols);
        subs.set(key, { nodeId: n.id, subId: s.id, x: b.x + K.GP + col * (K.SUB_TB.w + K.SG),
                        y: b.y + K.GH + row * (K.SUB_TB.h + K.SG), w: K.SUB_TB.w, h: K.SUB_TB.h });
      }
    });
  });

  // --- edges -------------------------------------------------------------
  const pairCount = new Map<string, number>();
  const pairSeen = new Map<string, number>();
  g.edges.forEach(e => {
    const k = pairKey(resolve(e.from), resolve(e.to));
    pairCount.set(k, (pairCount.get(k) || 0) + 1);
  });
  const crossMin = cStart;
  const crossMax = cStart + totalCross;
  const crossMid = (crossMin + crossMax) / 2;
  const buses = { min: 0, max: 0 };
  const skipCount = new Map<number, number>();
  const laneBlocks = byLane.map(list => list.map(n => mc.get(n.id)!).map(b => [b.c0 - 14, b.c1 + 14] as [number, number]));
  const blockedAt = (li: number, c: number) => laneBlocks[li].some(([lo, hi]) => c > lo && c < hi);
  const gapAfterLane = (li: number) => laneM0[li] + laneMain[li] + laneGap / 2;
  const gapBeforeLane = (li: number) => laneM0[li] - laneGap / 2;

  const edges = new Map<string, EdgeGeom>();
  g.edges.forEach(e => {
    const fromId = resolve(e.from); const toId = resolve(e.to);
    const A = mc.get(fromId); const B = mc.get(toId);
    if (!A || !B) return;
    const pk = pairKey(fromId, toId);
    const n = pairCount.get(pk) || 1;
    const i = pairSeen.get(pk) || 0;
    pairSeen.set(pk, i + 1);
    const off = (i - (n - 1) / 2) * 12;
    let cubics: Cubic[];
    let label: { at: P; anchor: 'start' | 'middle' | 'end' } | null = null;

    const bus = (side: 'min' | 'max') => {
      const k = buses[side]++;
      return side === 'max' ? crossMax + 20 + 12 * k : crossMin - 14 - 12 * k;
    };

    if (fromId === toId) {
      const s = { m: A.m1, c: A.cm - 8 }; const t = { m: A.m1, c: A.cm + 8 };
      cubics = [[s, { m: A.m1 + 40, c: A.cm - 34 }, { m: A.m1 + 40, c: A.cm + 34 }, t]];
    } else if (B.lane > A.lane) {
      const s = { m: A.m1, c: A.cm + off }; const t = { m: B.m0, c: B.cm + off };
      // Straight through when every lane in between is free where the line
      // crosses it; otherwise run along the outside of the graph (a "bus")
      // rather than threading between nodes.
      const mids: P[] = [];
      let blocked = false;
      for (let li = A.lane + 1; li < B.lane; li++) {
        const mid = laneM0[li] + laneMain[li] / 2;
        const frac = (mid - s.m) / Math.max(1, t.m - s.m);
        const c = s.c + frac * (t.c - s.c);
        if (blockedAt(li, c)) blocked = true;
        mids.push({ m: mid, c });
      }
      if (!blocked) {
        const pts = [s, ...mids, t];
        cubics = [];
        for (let k = 0; k < pts.length - 1; k++) cubics.push(sCurve(pts[k], pts[k + 1]));
      } else {
        const side = (s.c + t.c) / 2 >= crossMid ? 'max' : 'min';
        const bc = bus(side);
        const g1 = gapAfterLane(A.lane) - 6 + (buses[side] % 2) * 12;
        const g2 = gapBeforeLane(B.lane) + 6 - (buses[side] % 2) * 12;
        cubics = rounded([s, { m: g1, c: s.c }, { m: g1, c: bc }, { m: g2, c: bc }, { m: g2, c: t.c }, t], 16);
        label = { at: { m: (g1 + g2) / 2, c: bc }, anchor: 'middle' };
      }
    } else if (B.lane === A.lane) {
      const lo = Math.min(A.idx, B.idx); const hi = Math.max(A.idx, B.idx);
      if (hi - lo === 1) {
        const down = B.c0 >= A.c1;
        const s = { m: A.mm + off, c: down ? A.c1 : A.c0 };
        const t = { m: B.mm + off, c: down ? B.c0 : B.c1 };
        cubics = [line(s, t)];
        // beside the arrow, not on it: above/below a horizontal one, left/right of a vertical one
        const sign = off > 0 ? 1 : -1;
        label = { at: { m: A.mm + off + sign * (o === 'tb' ? (sign > 0 ? 13 : 5) : 7), c: (s.c + t.c) / 2 },
                  anchor: o === 'tb' ? 'middle' : sign > 0 ? 'start' : 'end' };
      } else {
        const k = skipCount.get(A.lane) || 0;
        skipCount.set(A.lane, k + 1);
        const bulge = laneM0[A.lane] + laneMain[A.lane] + 18 + 10 * k;
        const s = { m: A.m1, c: A.cm + off }; const t = { m: B.m1, c: B.cm + off };
        cubics = [[s, { m: bulge, c: s.c }, { m: bulge, c: t.c }, t]];
      }
    } else {
      const bc = bus('max');
      const outM = laneM0[A.lane] - 18; const inM = laneM0[B.lane] + laneMain[B.lane] + 18;
      cubics = rounded([
        { m: A.m0, c: A.cm }, { m: outM, c: A.cm }, { m: outM, c: bc },
        { m: inM, c: bc }, { m: inM, c: B.cm }, { m: B.m1, c: B.cm },
      ]);
    }

    // sample -> polyline for the dot and the label anchor
    const sampled: P[] = [];
    cubics.forEach((q, qi) => {
      for (let s = qi === 0 ? 0 : 1; s <= 16; s++) sampled.push(bez(q, s / 16));
    });
    const pts = sampled.map(toXY);
    const cum = [0];
    for (let k = 1; k < pts.length; k++) {
      cum.push(cum[k - 1] + Math.hypot(pts[k].x - pts[k - 1].x, pts[k].y - pts[k - 1].y));
    }
    const len = cum[cum.length - 1];
    const f = (v: number) => Math.round(v * 10) / 10;
    const d = cubics.map((q, qi) => {
      const [p0, p1, p2, p3] = q.map(toXY);
      return `${qi === 0 ? `M${f(p0.x)},${f(p0.y)} ` : ''}C${f(p1.x)},${f(p1.y)} ${f(p2.x)},${f(p2.y)} ${f(p3.x)},${f(p3.y)}`;
    }).join(' ');
    const geom: EdgeGeom = { id: e.id, edge: e, from: fromId, to: toId, d, pts, cum, len,
                             label: pts[0], labelAnchor: 'middle' };
    if (label) { geom.label = toXY(label.at); geom.labelAnchor = label.anchor; }
    else { const p = pointAt(geom, 0.5); geom.label = { x: p.x, y: p.y - 3 }; }
    edges.set(e.id, geom);
  });

  const extraMax = buses.max ? 26 + 12 * buses.max : 0;
  const crossTotal = crossMax + K.PAD + extraMax;
  const mainTotal = mEnd + K.PAD + (skipCount.size ? 30 : 0);
  const width = o === 'lr' ? mainTotal : crossTotal;
  const height = o === 'lr' ? crossTotal : mainTotal;

  const lanes: LaneBand[] = lanesUsed.map((l, li) => {
    const m0 = li === 0 ? K.PAD / 2 : laneM0[li] - laneGap / 2;
    const m1 = Math.min(laneM0[li] + laneMain[li] + laneGap / 2, mainTotal - K.PAD / 2);
    if (o === 'lr') {
      return { lane: l, label: LANE_LABELS[l] || l, index: li,
               box: { x: m0, y: K.PAD - 8, w: m1 - m0, h: height - K.PAD },
               labelAt: { x: laneM0[li] + laneMain[li] / 2, y: K.PAD + 8 } };
    }
    return { lane: l, label: LANE_LABELS[l] || l, index: li,
             box: { x: K.PAD - 8, y: m0, w: width - K.PAD, h: m1 - m0 },
             labelAt: { x: K.PAD + 4, y: laneM0[li] + laneMain[li] / 2 } };
  });

  return { layout: { orientation: o, width, height, nodes, subs, lanes, edges, parentOf }, minBuses: buses.min };
}

/** Point at fraction f (0..1) of an edge's length. */
export function pointAt(e: EdgeGeom, f: number): Pt {
  if (!e.pts.length) return { x: 0, y: 0 };
  const target = Math.max(0, Math.min(1, f)) * e.len;
  let lo = 0; let hi = e.cum.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (e.cum[mid] < target) lo = mid + 1; else hi = mid;
  }
  if (lo === 0) return e.pts[0];
  const a = e.pts[lo - 1]; const b = e.pts[lo];
  const span = e.cum[lo] - e.cum[lo - 1] || 1;
  const t = (target - e.cum[lo - 1]) / span;
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

export const centerOf = (b: Box): Pt => ({ x: b.x + b.w / 2, y: b.y + b.h / 2 });

// ---------------------------------------------------------------------------
// paths + timelines
// ---------------------------------------------------------------------------
/** Path ids as graph node ids: a sub-step id becomes its node, unknown ids go,
 *  and a node repeated back-to-back collapses to one visit. */
export function normalizePath(ids: string[] | undefined | null, g: FlowGraphData, parentOf: Map<string, string>): string[] {
  const known = new Set(g.nodes.map(n => n.id));
  const out: string[] = [];
  (ids || []).forEach(raw => {
    const id = String(raw);
    const slash = id.includes('/') ? id.split('/')[0] : id;
    const real = known.has(id) ? id : known.has(slash) ? slash : parentOf.get(id);
    if (!real) return;
    if (out[out.length - 1] !== real) out.push(real);
  });
  return out;
}

/** The edge taken between each consecutive pair of path nodes: the listed
 *  edge when it joins them, else any graph edge that does, else null (the
 *  dot then jumps in a straight line — a gap the server should close). */
export function resolvePathEdges(path: string[], listed: string[] | undefined, g: FlowGraphData,
                                 parentOf: Map<string, string>): (string | null)[] {
  const res = (id: string) => parentOf.get(id) || id;
  const byId = new Map(g.edges.map(e => [e.id, e]));
  const used = new Set<string>();
  const out: (string | null)[] = [];
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1]; const b = path[i];
    const joins = (e: FlowEdge | undefined) => !!e && res(e.from) === a && res(e.to) === b;
    let hit = (listed || []).find(id => !used.has(id) && joins(byId.get(id)));
    if (!hit) hit = g.edges.find(e => !used.has(e.id) && joins(e))?.id;
    if (!hit) hit = g.edges.find(e => joins(e))?.id;
    if (hit) used.add(hit);
    out.push(hit || null);
  }
  return out;
}

export interface Seg {
  kind: 'node' | 'edge';
  pathIdx: number;          // index of the node (for an edge: of its target)
  nodeId: string;           // node segment: the node; edge segment: the target
  fromId?: string;
  edgeId?: string | null;
  t0: number; t1: number;   // animation ms
  realStart: number;        // real pipeline ms at the start of this segment
  realMs: number;           // real ms this node took (0 for edges)
  step?: TraceStep | null;  // the node's own trace step, when replaying a run
}
export interface Timeline { segs: Seg[]; total: number; realTotal: number; path: string[] }

export function scenarioTimeline(path: string[], edgeIds: (string | null)[]): Timeline {
  const n = path.length;
  const beat = Math.max(240, Math.min(520, 6500 / Math.max(1, 2 * n - 1)));
  const segs: Seg[] = [];
  let t = 0;
  path.forEach((id, i) => {
    if (i > 0) {
      segs.push({ kind: 'edge', pathIdx: i, nodeId: id, fromId: path[i - 1], edgeId: edgeIds[i - 1],
                  t0: t, t1: t + beat, realStart: 0, realMs: 0 });
      t += beat;
    }
    segs.push({ kind: 'node', pathIdx: i, nodeId: id, t0: t, t1: t + beat, realStart: 0, realMs: 0 });
    t += beat;
  });
  return { segs, total: t, realTotal: 0, path };
}

/** Node steps of a trace in visit order: the k-th visit of a node takes the
 *  k-th node-level step for it (sub-step rows are the node's detail). */
export function nodeStepsFor(path: string[], steps: TraceStep[]): (TraceStep | null)[] {
  const pools = new Map<string, TraceStep[]>();
  steps.filter(s => !s.substep_id).forEach(s => {
    const list = pools.get(s.node_id) || [];
    list.push(s);
    pools.set(s.node_id, list);
  });
  const taken = new Map<string, number>();
  return path.map(id => {
    const list = pools.get(id) || [];
    const k = taken.get(id) || 0;
    taken.set(id, k + 1);
    return list[Math.min(k, list.length - 1)] || null;
  });
}

/** How long the search itself took: meta.timings.total_ms (what a caller
 *  waited), never less than the last node-level step's end, which is the
 *  fallback when the pipeline reported no total. Not took_ms: that is the
 *  trace's wall time, which on a quick route also includes up to 8 s spent
 *  AFTER the response waiting for the background expansion, plus the admin
 *  wrapper's overhead. */
export function realTotalOf(trace: FlowTrace): number {
  const ends = (trace.steps || []).filter(s => !s.substep_id)
    .map(s => (num(s.t_start_ms) ?? 0) + (num(s.ms) ?? 0));
  const lastEnd = Math.max(0, ...ends);
  const total = num(trace.timings?.total_ms);
  return total !== null && total > 0 ? Math.max(total, lastEnd) : lastEnd;
}

/** Replay of a real run: each node dwells in proportion to the ms it really
 *  took, scaled so the whole replay lasts 4-10 s at 1x (the real duration when
 *  it falls in that window). Every node gets a floor so a 0 ms step is still
 *  seen; edges get a short fixed travel. */
export function traceTimeline(path: string[], edgeIds: (string | null)[], trace: FlowTrace): Timeline {
  const n = path.length;
  const steps = nodeStepsFor(path, trace.steps || []);
  const realMs = steps.map(s => Math.max(0, num(s?.ms) ?? 0));
  const realTotal = realTotalOf(trace);
  const T = Math.max(4000, Math.min(10000, realTotal || 0));
  let floor = 200; let travel = 260;
  const fixed = n * floor + Math.max(0, n - 1) * travel;
  if (fixed > 0.55 * T) { const k = (0.55 * T) / fixed; floor *= k; travel *= k; }
  const budget = Math.max(0, T - (n * floor + Math.max(0, n - 1) * travel));
  const sumW = realMs.reduce((a, b) => a + b, 0);
  const segs: Seg[] = [];
  let t = 0;
  let realCursor = 0;
  path.forEach((id, i) => {
    const start = num(steps[i]?.t_start_ms);
    const rs = start ?? realCursor;
    if (i > 0) {
      segs.push({ kind: 'edge', pathIdx: i, nodeId: id, fromId: path[i - 1], edgeId: edgeIds[i - 1],
                  t0: t, t1: t + travel, realStart: realCursor, realMs: 0 });
      t += travel;
    }
    const dwell = floor + (sumW > 0 ? (budget * realMs[i]) / sumW : budget / Math.max(1, n));
    segs.push({ kind: 'node', pathIdx: i, nodeId: id, t0: t, t1: t + dwell, realStart: rs, realMs: realMs[i],
                step: steps[i] });
    t += dwell;
    realCursor = Math.max(realCursor, rs + realMs[i]);
  });
  return { segs, total: t, realTotal: realTotal || realCursor, path };
}

export type Phase = 'pending' | 'active' | 'done';
export interface Frame {
  node: Map<string, Phase>;
  edge: Map<string, Phase>;
  activeNode: string | null;
  activeSeg: Seg | null;
  dot: Pt | null;
  realNow: number;
  /** 0..1 progress of the active node segment */
  nodeFrac: number;
  finished: boolean;
}

const ease = (x: number) => (x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2);

export function frameAt(tl: Timeline | null, t: number, layout: Layout): Frame {
  const node = new Map<string, Phase>(); const edge = new Map<string, Phase>();
  const empty: Frame = { node, edge, activeNode: null, activeSeg: null, dot: null, realNow: 0, nodeFrac: 0, finished: false };
  if (!tl || !tl.segs.length) return empty;
  tl.path.forEach(id => node.set(id, 'pending'));
  let active: Seg | null = null;
  for (const s of tl.segs) {
    const done = t >= s.t1;
    const on = !done && t >= s.t0;
    if (s.kind === 'node') {
      if (done) node.set(s.nodeId, 'done');
      else if (on) { node.set(s.nodeId, 'active'); active = s; }
    } else if (s.edgeId) {
      if (done) edge.set(s.edgeId, 'done');
      else if (on) { edge.set(s.edgeId, 'active'); active = s; }
      else if (!edge.has(s.edgeId)) edge.set(s.edgeId, 'pending');
    } else if (on) {
      active = s;
    }
  }
  const finished = t >= tl.total;
  let dot: Pt | null = null;
  let nodeFrac = 0;
  let realNow = finished ? tl.realTotal : 0;
  if (active) {
    const f = (t - active.t0) / Math.max(1, active.t1 - active.t0);
    if (active.kind === 'node') {
      const b = layout.nodes.get(active.nodeId);
      dot = b ? centerOf(b) : null;
      nodeFrac = f;
      realNow = active.realStart + f * active.realMs;
    } else {
      const g = active.edgeId ? layout.edges.get(active.edgeId) : null;
      if (g) dot = pointAt(g, ease(f));
      else {
        const a = layout.nodes.get(active.fromId || ''); const b = layout.nodes.get(active.nodeId);
        if (a && b) {
          const p = centerOf(a); const q = centerOf(b); const e = ease(f);
          dot = { x: p.x + (q.x - p.x) * e, y: p.y + (q.y - p.y) * e };
        }
      }
      realNow = active.realStart;
    }
  } else if (finished) {
    const last = tl.segs[tl.segs.length - 1];
    const b = last ? layout.nodes.get(last.nodeId) : null;
    dot = b ? centerOf(b) : null;
  }
  return { node, edge, activeNode: active?.kind === 'node' ? active.nodeId : null, activeSeg: active,
           dot, realNow, nodeFrac, finished };
}
